import React from "react";
import { Link2, Globe, Phone } from "lucide-react";
import {
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaSquareYoutube,
  FaSnapchat,
  FaLinkedin,
  FaTelegram,
  FaWhatsapp,
  FaTwitter,
  FaViber,
  FaBehance,
  FaGithub,
} from "react-icons/fa6";

// Lucide has no brand logos, so brands come from react-icons and generic glyphs from Lucide.
// Keys are the Font Awesome names already stored in the database (links.icon), so saved links keep working.
const ICONS = {
  "fa-facebook": FaFacebook,
  "fa-instagram": FaInstagram,
  "fa-tiktok": FaTiktok,
  "fa-youtube": FaYoutube,
  "fa-square-youtube": FaSquareYoutube,
  "fa-snapchat": FaSnapchat,
  "fa-linkedin": FaLinkedin,
  "fa-telegram": FaTelegram,
  "fa-whatsapp": FaWhatsapp,
  "fa-twitter": FaTwitter,
  "fa-viber": FaViber,
  "fa-behance": FaBehance,
  "fa-github": FaGithub,
  "fa-globe": Globe,
  "fa-phone": Phone,
  "fa-link": Link2,
};

// Font Awesome style/prefix tokens and the "tell" phone marker carry no styling of their own.
const IGNORED = new Set(["fa", "fas", "fab", "far", "fa-solid", "fa-brands", "fa-regular", "tell"]);

// A text colour class (text-pink-600, text-black) as opposed to a size (text-xs, text-2xl, text-[11px]).
const COLOR_CLASS = /^text-(?!((xs|sm|base|lg|xl|\dxl|left|right|center|justify)$|\[))/;

// Black or white, whichever reads better on the given hex / rgb(a) background.
const contrastOn = (bg) => {
  let rgb = null;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((bg || "").trim());
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, "$&$&") : hex[1];
    rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  } else {
    const m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec((bg || "").trim());
    if (m) rgb = [m[1], m[2], m[3]].map(Number);
  }
  if (!rgb) return "#000000";
  const [r, g, b] = rgb;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#000000" : "#ffffff";
};

// Renders the icon for a stored value such as "fab fa-instagram text-pink-600".
// Sized 1em so it follows the surrounding font-size (text-xs, text-lg, ...) and scales with it;
// leftover classes (text colours) are passed through, and an explicit colour prop wins.
// Icons with no colour of their own (the globe) turn black or white to suit `onBackground`.
const LinkIcon = ({ value, className = "", color, onBackground, size = "1em" }) => {
  const tokens = (value || "").split(/\s+/).filter(Boolean);
  const name = tokens.find((tok) => ICONS[tok]);
  const Icon = ICONS[name] || Link2;
  const rest = tokens.filter((tok) => tok !== name && !IGNORED.has(tok));
  const ownColor = color || (rest.some((tok) => COLOR_CLASS.test(tok)) ? undefined : onBackground && contrastOn(onBackground));
  return (
    <Icon
      size={size}
      color={ownColor}
      className={[...rest, className].join(" ").trim() || undefined}
      aria-hidden="true"
    />
  );
};

export default LinkIcon;
